/**
 * Figure-counter context — assigns sequential, report-global figure numbers.
 *
 * THE BUG IT KILLS: figure captions (PropertyMapContent, EditorialFigureCaption
 * via EditorialExhibit) defaulted their `figureNumber`/`captionNumber` to `1`,
 * so every figure in a report rendered as "Figure 1." A report with a cover
 * site map, a key-findings map, and three section exhibits showed five
 * "Figure 1"s. This context hands out the next sequential number instead.
 *
 * Why a context (mirrors `viewMode.tsx`): figures are scattered a dozen levels
 * deep across the report tree (cover, overview section, decomposed sections).
 * Threading a running counter as a prop through every intermediate component is
 * miserable; one provider at the report root reaches them all.
 *
 * Why KEY-BASED (not a raw mutable counter): React renders are not guaranteed
 * to run once — StrictMode double-invokes, and any subtree re-render would
 * re-bump a naive counter and reshuffle numbers. Instead each figure registers
 * a STABLE key (e.g. the section id, or `siteMap:cover`). The first time a key
 * is seen it is assigned the next number; the SAME key always returns the SAME
 * number thereafter. This makes numbering:
 *   - deterministic in first-render order,
 *   - idempotent across re-renders / StrictMode double-invoke,
 *   - stable for a given figure even when unrelated siblings re-render.
 *
 * NO PROVIDER (legacy / no-context) ⇒ `useFigureNumber` returns the caller's
 * `fallback` (default 1), so components outside a report root render exactly as
 * before — byte-stable for any standalone / test mount.
 */
import React from 'react';

/**
 * Redhead review wave 3 (defect 5) — figures and tables number on SEPARATE
 * report-global sequences ("Figure 1, 2, 3…" and "Table 1, 2, 3…"), matching
 * academic-editorial convention. Every existing caller numbers on the
 * 'figure' channel (the default), so their numbering is unchanged.
 */
export type FigureCounterChannel = 'figure' | 'table';

interface FigureCounterApi {
  /** Assign (or recall) the 1-based number for a stable figure key. */
  assign: (key: string, channel?: FigureCounterChannel) => number;
}

const FigureCounterContext = React.createContext<FigureCounterApi | null>(null);

export interface FigureCounterProviderProps {
  children: React.ReactNode;
}

/**
 * Provider — owns the key→number map for one report render. Reset on mount via
 * a fresh ref so each report root starts its figure sequence at 1.
 */
export function FigureCounterProvider({ children }: FigureCounterProviderProps): React.ReactElement {
  // Stable across re-renders; first-seen key order defines the numbering.
  // One key→number map PER CHANNEL so tables number independently of figures.
  const mapsRef = React.useRef<Map<FigureCounterChannel, Map<string, number>>>(new Map());

  const api = React.useMemo<FigureCounterApi>(() => ({
    assign: (key: string, channel: FigureCounterChannel = 'figure') => {
      let map = mapsRef.current.get(channel);
      if (!map) {
        map = new Map<string, number>();
        mapsRef.current.set(channel, map);
      }
      const existing = map.get(key);
      if (existing !== undefined) return existing;
      const next = map.size + 1;
      map.set(key, next);
      return next;
    },
  }), []);

  return <FigureCounterContext.Provider value={api}>{children}</FigureCounterContext.Provider>;
}

/**
 * Resolve the sequential figure number for `key`. Outside a provider (no report
 * root in the tree — standalone mounts, unit tests) returns `fallback` so the
 * caller renders exactly as it did before the counter existed.
 *
 * `key: null` skips assignment entirely (returns `fallback`) so a component
 * that only SOMETIMES participates in the sequence can call the hook
 * unconditionally without consuming a number.
 */
export function useFigureNumber(
  key: string | null,
  fallback = 1,
  channel: FigureCounterChannel = 'figure',
): number {
  const ctx = React.useContext(FigureCounterContext);
  // Hooks must be called unconditionally; ctx may legitimately be null.
  return ctx && key != null ? ctx.assign(key, channel) : fallback;
}
